/**
 * Row maths for the legacy Analytics -> Reports summary tables.
 *
 * Each table row is [groupKey, rows[]] where rows are the grouped API rows for
 * that group (one per state / status). Summing `count_id` across those rows
 * counts a lead once per state it has ever been in, and summing the revenue
 * multiplies a treatment's price by the number of states it passed through.
 */
export const STATE_KEY = 'lead_treatments__state_transition_logs__state';

const sum = (rows, key) => (rows || []).reduce((total, r) => total + (Number(r?.[key]) || 0), 0);

// "All" / Enquiries = distinct leads. `lead_count` is attached by the slice from
// a dedicated lead-count query; the sum-of-rows fallback only applies when that
// query is unavailable (then it is an upper bound, never a headline number).
export const rowLeadCount = (rows) => {
    const withCount = (rows || []).find((r) => r && r.lead_count !== undefined && r.lead_count !== null);
    if (withCount) return Number(withCount.lead_count) || 0;
    return sum((rows || []).filter((r) => r?.lead_treatments__status !== null), 'count_id');
};

// Completed revenue: price of the CLOSED row only, never summed across states.
export const rowClosedRevenue = (rows, stateKey = STATE_KEY) =>
    sum((rows || []).filter((r) => r?.[stateKey] === 'CLOSED'), 'sum_lead_treatments__custom_price');

export const rowStateCount = (rows, state, stateKey = STATE_KEY) =>
    sum((rows || []).filter((r) => r?.[stateKey] === state), 'count_id');

export const CATEGORY_NAME_KEY = 'lead_treatments__treatment__treatment_item__treatment_category__name';

// Category label for a table row [categoryId, rows[]]: the name that rides along on the
// grouped rows first, then the practice-treatment list lookup as a fallback.
export const rowCategoryName = (rows, categoryId, treatmentResults = []) => {
    const fromRows = (rows || []).find((r) => r?.[CATEGORY_NAME_KEY])?.[CATEGORY_NAME_KEY];
    if (fromRows) return fromRows;
    return (treatmentResults || []).find((el) => el?.treatment_item?.treatment_category?.id === Number(categoryId))
        ?.treatment_item?.treatment_category?.name || '';
};

const CATEGORY_ID_KEY = 'lead_treatments__treatment__treatment_item__treatment_category';

// The Category Summary table is built from stage-movement rows, so a category whose
// enquiries never moved past NEW got no row at all (ring cards showed it, table did not).
// Append a row for every category that has enquiries but no table row yet. The placeholder
// carries only the name; the row's counts come from the enquiry / not-booked lookups.
export const withEnquiryOnlyCategories = (tableData = [], enquiryRows = []) => {
    const have = new Set((tableData || []).map(([id]) => String(id)));
    const extra = [];
    (enquiryRows || []).forEach((r) => {
        const id = r?.[CATEGORY_ID_KEY];
        if (id === null || id === undefined || !(Number(r?.count_id) > 0) || have.has(String(id))) return;
        have.add(String(id));
        extra.push([String(id), [r?.[CATEGORY_NAME_KEY] ? { [CATEGORY_NAME_KEY]: r[CATEGORY_NAME_KEY] } : {}]]);
    });
    return extra.length ? [...(tableData || []), ...extra] : tableData;
};
